import type { ProfileInfoPayload } from '~/api/clients/consumer/data-contracts'

// TODO better type support for Profile Payloads GLOBAL15-106267. ProfileInfoData['consumerProfile']['consumerDetails'] can't be used because it's optional
type NewsletterPayload = {
  optIn?: boolean
  loyaltyEnroll?: boolean
  loyaltyOptIn?: boolean
  firstName?: string
  lastName?: string
  email?: string
  birthDate?: string
  interests?: string
  gender?: 'M' | 'F' | 'NA'
  acquisitionType?: string
  campaignName?: string
  recipient?: string
  sms?: {
    phone: string
    phoneCode: string
  }
}

/**
 * Checks if an existing SMS subscription should be unsubscribed before creating a new one.
 *
 * @param isGuest - True if user is not logged in
 * @param smsNumber - Current subscribed SMS number
 * @param optIn - True if user is opting in (subscribing)
 * @returns True if registered user has existing subscription and is opting in (requires unsubscribe-then-resubscribe flow)
 *
 * @remarks
 * Required for registered users changing their SMS number: must unsubscribe old number before subscribing new one
 */
const shouldUnsubscribe = (isGuest, smsNumber, optIn) => !isGuest && smsNumber && optIn

/**
 * Loads user profile if user is logged in and profile not already cached.
 *
 * @param store - User store instance
 * @param isGuest - True if user is not logged in
 *
 * @remarks
 * Client-only operation; skipped during SSR
 */
const loadProfile = async (store, isGuest) => {
  if (isClient() && !store.profile && !isGuest) await store.get()
}

/**
 * Refreshes user profile after subscription changes (for logged-in users only).
 *
 * @param store - User store instance
 * @param isGuest - True if user is not logged in
 *
 * @remarks
 * Client-only operation; ensures UI reflects updated subscription status
 */
const updateProfile = async (store, isGuest) => {
  if (isClient() && !isGuest) await store.get()
}

type SubscriptionPayload = Exclude<Exclude<ProfileInfoPayload['consumerProfile'], undefined>['subscriptions'], undefined>[number]

/**
 * Formats profile payload for Consumer API subscription endpoints.
 *
 * @param subscriptions - Array of subscription objects
 * @param consumerDetails - Optional consumer details (name, email, birthdate, etc.)
 * @returns ProfileInfoPayload formatted for Consumer API
 */
const getProfilePayload = (subscriptions: SubscriptionPayload[], consumerDetails = {}): ProfileInfoPayload => ({
  consumerProfile: {
    consumerDetails,
    subscriptions,
  }
})

/**
 * Manages email and SMS subscription preferences via Consumer API.
 *
 * Handles:
 * - Newsletter (email) subscription/unsubscription with interests, loyalty enrollment, and campaign tracking
 * - SMS subscription/unsubscription with E.164 phone formatting
 * - Guest vs. registered user flows (create vs. update profile)
 * - Automatic unsubscribe-then-resubscribe for registered users changing SMS numbers
 * - Loyalty newsletter opt-in
 * - Profile updates after subscription changes (for logged-in users)
 *
 * @returns Subscription state and actions: `smsNumber`, `smsSubscription`, `sms`, `newsletterSubscribed`, `loyaltyNewsletterSubscribed`, `newsletter`, and `consumerId`
 *
 * @example
 * ```vue
 * <script setup>
 * const { sms, newsletter, newsletterSubscribed, smsNumber } = useSubscriptions()
 *
 * // Subscribe to SMS
 * await sms('+1234567890', 'US', true)
 *
 * // Subscribe to newsletter with loyalty
 * await newsletter({
 *   optIn: true,
 *   email: 'user@example.com',
 *   firstName: 'John',
 *   loyaltyEnroll: true,
 *   interests: 'hiking,running'
 * })
 * </script>
 * ```
 *
 * @remarks
 * - For registered users, profile is automatically loaded on composable init (client-only)
 * - SMS subscriptions require E.164 phone format (`formatE164phone` used internally)
 * - Registered users changing SMS numbers trigger automatic unsubscribe → resubscribe flow
 * - Newsletter interests are cleared on unsubscribe if `interestOnlyForSubscription.requiredLocales` includes current locale
 * - Loyalty enrollment and loyalty newsletter opt-in are independent flags
 * - Guest newsletter signups return a `consumerId` (used for GTM tracking)
 * - All subscription dates use server time (via `getServerDate()`) to avoid client clock drift
 */
export default () => {
  const { interestOnlyForSubscription } = useAppConfig().pages.profile
  const { consumer } = useApi()
  const auth = useAuthStore()
  const isGuest = computed(() => !auth.loggedIn)

  const store = useUserStore()
  loadProfile(store, isGuest.value) // conditionally loads the profile if it's not already loaded

  const smsNumber = computed(() => store.subscriptions.sms?.recipient)
  const smsSubscription = computed(() => store.subscriptions.sms)
  const newsletterSubscribed = computed(() => !!store.profile?.subscriptions?.find(({ type, channel }) => type === 'Newsletter' && channel === 'email')?.optin)
  const loyaltyNewsletterSubscribed = computed(() => !!store.profile?.subscriptions?.find(({ type }) => type === 'LoyaltyNewsletter')?.optin)

  const consumerId = ref('')

  /**
   * Subscribes or unsubscribes a phone number to SMS newsletter.
   *
   * For registered users with an existing SMS subscription, automatically unsubscribes the old number
   * before subscribing the new one (required by Consumer API). For guests, creates a new subscription.
   * Phone numbers are formatted to E.164 before submission.
   *
   * @param phone - Phone number (without country code prefix, e.g., "5551234567")
   * @param phoneCode - Country code (e.g., "US", "CA", "GB")
   * @param optIn - True to subscribe, false to unsubscribe
   *
   * @example
   * ```ts
   * // Subscribe
   * await sms('5551234567', 'US', true)
   *
   * // Unsubscribe
   * await sms('5551234567', 'US', false)
   * ```
   *
   * @remarks
   * - For registered users changing numbers: old number is unsubscribed, then new number is subscribed
   * - Uses server date for optin/optout timestamps to avoid client clock issues
   * - Profile is reloaded after subscription change (registered users only)
   */
  async function sms(phone: string, phoneCode: string, optIn = true) {
    const serverDate = utcIsoDate(await getServerDate())

    const subscriptionPayload: any = {
      type: 'Newsletter',
      optin: optIn,
      doubleOptin: false,
      channel: 'sms',
      // the user can change the number in the form if not replied 'Y' to the SMS,
      // so we need to ensure we unsubscribe the previous subscribed number, not the field input
      recipient: !optIn ? smsNumber.value : formatE164phone(phone, phoneCode),
      category: 'subscription',
      country: useCountryCode(),
      region: useRegion(),
      [optIn ? 'optinDate' : 'optoutDate']: serverDate
    }

    // if a registered user - has a previous subscription - and is opting in,
    // we need to unsubscribe first
    if (shouldUnsubscribe(isGuest.value, smsNumber.value, optIn)) {
      await consumer.$updateSmsSubscription(getProfilePayload([{
        ...subscriptionPayload,
        optin: false,
        recipient: smsNumber.value,
        optoutDate: serverDate
      }]))
    }

    const subscriptionAction = isGuest.value
      ? consumer.$createSmsSubscription
      : consumer.$updateSmsSubscription
    await subscriptionAction(getProfilePayload([subscriptionPayload]))

    // for registered users, reload the profile
    await updateProfile(store, isGuest.value)
  }

  /**
   * Subscribes or unsubscribes to email newsletter, with optional loyalty enrollment and interests.
   *
   * For guests, creates a consumer profile if `email` is provided and returns the new `consumerId`.
   * For registered users, updates the existing profile. Supports loyalty program enrollment (separate from
   * newsletter), loyalty newsletter opt-in, and user interests (hiking, running, etc.). Interests are
   * cleared on unsubscribe if the current locale requires it (`interestOnlyForSubscription.requiredLocales`).
   *
   * @param data - Newsletter subscription data
   * @param data.optIn - True to subscribe, false to unsubscribe (default: true)
   * @param data.loyaltyEnroll - True to enroll in loyalty program (independent of newsletter)
   * @param data.loyaltyOptIn - True to subscribe to loyalty-specific newsletter
   * @param data.firstName - User's first name
   * @param data.lastName - User's last name
   * @param data.email - User's email (required for guest signups)
   * @param data.birthDate - ISO date string (YYYY-MM-DD)
   * @param data.interests - Comma-separated interests (e.g., "hiking,running")
   * @param data.gender - Gender ('M', 'F', 'NA')
   * @param data.acquisitionType - Acquisition source type (for marketing attribution)
   * @param data.campaignName - Campaign name (for marketing attribution)
   * @param data.recipient - Email address associated with the newsletter subscription
   * @param data.sms - Optional SMS subscription data to include in the same profile request
   * @returns Promise resolving to consumer ID (non-empty only for guest signups with email)
   *
   * @example
   * ```ts
   * // Guest signup with loyalty
   * const id = await newsletter({
   *   optIn: true,
   *   email: 'user@example.com',
   *   firstName: 'Jane',
   *   loyaltyEnroll: true,
   *   interests: 'running,yoga'
   * })
   *
   * // Registered user unsubscribe
   * await newsletter({ optIn: false })
   * ```
   *
   * @remarks
   * - Guest signups require `email`; registered user updates use existing email
   * - Loyalty enrollment and loyalty newsletter are independent options
   * - Interests are cleared on unsubscribe if locale is in `interestOnlyForSubscription.requiredLocales`
   * - Profile is reloaded after subscription change (registered users only)
   * - Uses server date for optin/optout timestamps
   */
  async function newsletter(data: NewsletterPayload) {
    const {
      optIn = true,
      loyaltyEnroll,
      loyaltyOptIn,
      firstName = '',
      lastName = '',
      email = '',
      birthDate = '',
      interests = '',
      gender,
      acquisitionType = '',
      campaignName = '',
      recipient = '',
      sms
    } = data
    const serverDate = utcIsoDate(await getServerDate())
    const region = useRegion()
    const subscriptions: SubscriptionPayload[] = (newsletterSubscribed.value || optIn)
      ? [{
          type: 'Newsletter',
          channel: 'email',
          ...(recipient && { recipient }),
          optin: optIn,
          doubleOptin: optIn,
          country: useCountryCode(),
          region,
          [!optIn ? 'optoutDate' : 'optinDate']: serverDate
        }]
      : []

    if (optIn && sms) {
      subscriptions.push({
        type: 'Newsletter',
        channel: 'sms',
        recipient: formatE164phone(sms.phone, sms.phoneCode),
        optin: true,
        doubleOptin: false,
        category: 'subscription',
        country: useCountryCode(),
        region,
        optinDate: serverDate
      })
    }

    const payload: any = {
      consumerDetails: {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: email || undefined,
        birthDate: birthDate || undefined,
        gender: gender || undefined
      },
      enrollments: loyaltyEnroll ? [{ type: 'Loyalty', optin: true, doubleOptin: true, country: useCountryCode() }] : undefined,
      subscriptions
    }

    if (optIn && interests) payload.preferences = { interests }

    // need to remove the interests if the user is opting out in the required locale
    if (!optIn && interestOnlyForSubscription?.requiredLocales.includes(useLocale()))
      payload.preferences = { interests: '' }

    if (loyaltyOptIn !== undefined) {
      payload.subscriptions.push({
        type: 'LoyaltyNewsletter',
        optin: loyaltyOptIn,
        doubleOptin: loyaltyOptIn,
        region,
        channel: 'email',
        [!loyaltyOptIn ? 'optoutDate' : 'optinDate']: serverDate
      })
    }
    const body = { consumerProfile: payload }
    if (acquisitionType || campaignName) {
      payload.consumerDetails.source = {
        ...(acquisitionType && { acquisitionType }),
        ...(campaignName && { campaignName })
      }
    }

    if (email)
      consumerId.value = (await consumer.$createProfile(body)).consumerId || ''
    else
      await consumer.$updateProfile(body)

    // for registered users, reload the profile
    await updateProfile(store, isGuest.value)

    // return consumer id as it is necessary elsewhere, like GTM
    return consumerId.value
  }

  return {
    smsNumber,
    smsSubscription,
    sms,
    newsletterSubscribed,
    loyaltyNewsletterSubscribed,
    newsletter,
    consumerId
  }
}
